import { useState } from "react";
import { Control, FieldValues, Path, useController } from "react-hook-form";
import { Flex, Text } from "styles/utility-components";

import Button from "components/ui/button/Button";
import Tooltip from "components/ui/tooltip/Tooltip";
import { SignalPreviewCountsByKey } from "lib/hooks/api/signals/useSignalPreviewCounts";
import { TextSearchTipsModal } from "../../../modals/TextSearchTipsModal";
import TagInput from "../../../ui/tag_input/TagInput";
import ClearAllSignalButton from "../ClearAllSignalButton";
import SignalMatchCount from "../SignalMatchCount";
import KeywordInputTooltip from "./keyword_input_tooltip/KeywordInputTooltip";
import SuggestedKeywordsInput from "./suggested_keywords_input/SuggestedKeywordsInput";

const KEYWORD_WARNING_THRESHOLD = 500;

type KeywordInputProps<T extends FieldValues> = {
  name: Path<T>;
  control: Control<T, unknown>;
  resultCounts: SignalPreviewCountsByKey | undefined;
  isLoadingCounts?: boolean;
};

export default function KeywordInput<T extends FieldValues>({
  name,
  control,
  resultCounts,
  isLoadingCounts = false,
}: KeywordInputProps<T>) {
  const { fieldState } = useController({ name, control });
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleOpen = () => {
    setIsModalOpen(true);
  };

  const handleClose = () => {
    setIsModalOpen(false);
  };

  const handleGenerateLabel = (tag: string) => {
    const count = resultCounts?.[tag];
    const warning = count && count > KEYWORD_WARNING_THRESHOLD;
    const hasCount = count !== undefined;

    const label = (
      <Flex gap={4}>
        {tag}
        <SignalMatchCount count={count} isLoadingCounts={isLoadingCounts} />
      </Flex>
    );

    // Tooltip covers the whole tag (not just the number), and only when we have a count.
    if (!hasCount) {
      return label;
    }

    return (
      <Tooltip content={<KeywordInputTooltip count={count} warning={warning} />}>{label}</Tooltip>
    );
  };

  const handleVariantSelector = (tag: string) => {
    const count = resultCounts?.[tag];
    if (count && count > KEYWORD_WARNING_THRESHOLD) {
      return "warning";
    }
    return "active";
  };

  return (
    <Flex column gap={16} width="100%" style={{ marginTop: 24 }}>
      <SuggestedKeywordsInput name={name} control={control} />
      <Flex column gap={4}>
        <TagInput
          name={name}
          control={control}
          variant="active"
          generateLabel={handleGenerateLabel}
          variantSelector={handleVariantSelector}
          placeholder="Search or paste a comma-separated list"
        />
        {fieldState.error?.message && (
          <Text description inline color="sysDestructiveDefault">
            {fieldState.error.message}
          </Text>
        )}
      </Flex>
      <Flex justifyContent="space-between" gap={8}>
        <ClearAllSignalButton name={name} control={control} />
        <Button buttonType="secondary" onClick={handleOpen} icon="lightbulb03">
          Advanced keyword tips
        </Button>
      </Flex>
      <TextSearchTipsModal isOpen={isModalOpen} onClose={handleClose} />
    </Flex>
  );
}
